import { useMemo } from 'react';

import { BUSINESS_TOOL_SECTIONS, DIMENSIONALITY_VALUES, IDimensionalityOption } from 'constants/businessTool';
import { CURRENCY_LIST, CurrencyType } from 'constants/Currencies';

import { IOptionType } from 'interfaces/forms';

import {
  initialPeriodsTableHeadingRow,
  IPeriodNames,
  IPeriodsTableRow,
  PERIOD_NAMES,
  useGetInitialPeriodsTableHeadingRow,
} from 'components/BusinessToolForm/constants/periods';

import { concatWithDot } from 'helpers/string';

import { IGlobalProductEntity, productInitialValues } from './general/products';

export const globalPrefix = `${BUSINESS_TOOL_SECTIONS.GLOBAL}` as const;

export const STRENGTH_WEAKNESSES_NAMES = {
  TITLE: 'title',
} as const;

export const GLOBAL_FIELD_NAMES = {
  COMPANY_NAME: 'companyName',
  FINANCED_PORTION_COST: 'financedPortionCost',
  REGISTRATION_DATE: 'registrationDate',
  ADDRESS: 'address',
  COMPANY_BRIEF_DESCRIPTION: 'companyBriefDescription',
  COMPANY_SECTOR: 'companySector',
  COMPANY_SUB_SECTOR: 'companySubSector',
  COMPANY_INDUSTRY_GROUP: 'industryGroup',
  COMPANY_INDUSTRY: 'companyIndustry',
  NUMBER_OF_EMPLOYEES: 'numberOfEmployees',
  ANNUAL_INCOME: 'annualIncome',
  PROJECT_BRIEF_DESCRIPTION: 'projectBriefDescription',
  COVER_TITLE: 'coverTitle',
  MAIN_PRODUCTS_OR_SERVICES: 'mainProductsOrServices',
  PERIOD: 'period',
  PERIOD_TABLE_HEADINGS: 'periodTableHeadings',
  CURRENCY: 'currency',
  UNIT: 'unit',
  MAIN_GOALS: 'mainGoals',
  STRATEGY_FOR_ACHIEVING_GOALS: 'strategyForAchievingGoals',
  TARGET_MARKET: 'targetMarket',
  TARGET_AUDIENCE: 'targetAudience',
  TARGET_CHANNELS: 'targetChannels',
  MAIN_COMPETITORS: 'mainCompetitors',
  PROJECTED_ANNUAL_INCOME: 'projectedAnnualIncome',
  FORECASTED_PROFITABILITY: 'forecastedProfitability',
  PROJECT_PAYBACK_PERIOD: 'projectPaybackPeriod',
  TOTAL_COST: 'totalCost',
  FUNDING_AMOUNT: 'fundingAmount',
  PERCENTAGE_OF_FUNDING: 'percentageOfFunding',
  MISSION: 'mission',
  COMPANY_STRENGTHS: 'companyStrengths',
  COMPANY_WEAKNESSES: 'companyWeaknesses',
  PRODUCTS: 'products',
  COMPETITIVE_ADVANTAGE: 'competitionAdvantage',
  PRODUCT_DEVELOPMENT_PLANS: 'productDevelopmentPlans',
  LOAN_BORROW_AMOUNT: 'loanBorrowAmount',
  INVESTMENT_AMOUNT: 'investmentAmount',
  REVENUE_STREAMS: 'revenueStreams',
  MARKETING_STRATEGY: 'marketingStrategy',
  IS_JUST_CREATED: 'isJustCreated',
  DOCUMENT_LANGUAGE: 'documentLanguage',
} as const;

export const ALL_FIELD_NAMES = {
  COMPANY_DESCRIPTION: {
    COMPANY_OWNERS: 'companyDescription.companyOwners',
    HISTORY: 'companyDescription.history',
    RESOURCES_AND_ASSETS: 'companyDescription.resourcesAndAssets',
  },
  PRODUCTS_OR_SERVICES: {
    PRODUCTS_VALUE: 'productsOrServices.productsValue',
  },
  MARKET_AND_CUSTOMERS: {
    DYNAMICS: 'marketAndCustomers.dynamics',
  },
  ORGANIZATION_AND_TEAM: {
    MEMBERS: 'organizationAndTeam.members',
    MANAGEMENT_TEAM_HISTORY: 'managementTeam.history',
    PERSONNEL_PROFILES: 'managementTeam.personnelProfiles',
  },
  FINANCIAL_INFORMATION: {
    ASSETS_SIZE: 'financialInformation.assetsSize',
    AVERAGE_ANNUAL_PROFIT: 'financialInformation.averageAnnualProfit',
    PROJECTED_ANNUAL_PROFIT: 'financialInformation.projectedAnnualProfit',
    RETURN_ON_INVESTMENT: 'financialInformation.returnOnInvestment',
    INTERNAL_RATE_OF_RETURN: 'financialInformation.internalRateOfReturn',
  },
  FINANCIAL_PERFORMANCE: {
    INVESTMENT: {
      INVESTMENT_FORECASTED_RESULT: 'financialPerformance.investment.investmentForecastedResult',
    },
  },
  MARKET_OVERVIEW: {
    MAIN_MARKET_OVERVIEW: 'marketOverview.mainMarketOverview',
    FORECASTED_CONSUMPTION_CHART: 'marketOverview.forecastedConsumptionChart',
    CURRENT_MARKET_STRUCTURE: 'marketOverview.currentMarketStructure',
    AVERAGE_PRICES_CHART: {
      AVERAGE_PRICES_REGIONS_ROWS: 'marketOverview.averagePricesChart.averagePricesRegionsRows',
    },
    SERVICES_PRODUCTS_SUMMARY: 'marketOverview.servicesProductsSummary',
    ACTUAL_AND_FORECASTED_CONSUMPTION: 'marketOverview.actualAndForecastedConsumption',
    MARKET_TRENDS: 'marketOverview.marketTrends',
    SALES_METHOD: 'marketOverview.salesMethod',
    MARKET_STRUCTURE_DESCRIPTION: 'marketOverview.marketStructureDescription',
    MARKET_GROWTH_FACTORS: 'marketOverview.marketGrowthFactors',
    PRICE_STRATEGIES: 'marketOverview.priceStrategies',
    AVERAGE_PRICES_TITLE: 'marketOverview.averagePricesTitle',
    CONSUMERS_DESCRIPTION: 'marketOverview.consumersDescription',
    MARKET_COMPETITORS: 'marketOverview.marketCompetitors',
    MARKET_SHARE: 'marketOverview.marketShare',
    TECHNOLOGY_IMPACT: 'marketOverview.technologyImpact',
    AVERAGE_PRICES_DATE: 'marketOverview.averagePricesDate',
  },
  CONSUMER_NEEDS: {
    CUSTOMER_BASE_SIZE: 'consumerNeeds.customerBaseSize',
    HOW_CUSTOMERS_SATISFIED: 'consumerNeeds.howCustomersSatisfied',
    DEMAND: 'consumerNeeds.demand',
    PURCHASING_DECISION: 'consumerNeeds.purchasingDecision',
    INFLUENCING_FACTORS: 'consumerNeeds.influencingFactors',
    DIFFERENCES: 'consumerNeeds.differences',
    SURVEY: 'consumerNeeds.survey',
    B2B: {
      INDUSTRY: 'consumerNeeds.B2B.industry',
      ANNUAL_REVENUE: 'consumerNeeds.B2B.annualRevenue',
      NUMBER_OF_EMPLOYERS: 'consumerNeeds.B2B.numberOfEmployers',
      REGION: 'consumerNeeds.B2B.region',
      CUSTOMER_BUYING_BEHAVIOR: 'consumerNeeds.B2B.customerBuyingBehavior',
    },
    B2C: {
      STAGE: {
        VALUE: 'consumerNeeds.B2C.stage.value',
        LABEL: 'consumerNeeds.B2C.stage.label',
      },
      AGE: 'consumerNeeds.B2C.age',
      ANNUAL_INCOME: 'consumerNeeds.B2C.annualIncome',
      NEEDS_AND_PROBLEMS: 'consumerNeeds.B2C.needsAndProblems',
      PERSONAL_INTERESTS: 'consumerNeeds.B2C.personalInterests',
      PROFESSION: 'consumerNeeds.B2C.profession',
      PURCHASING_HABITS: 'consumerNeeds.B2C.purchasingHabits',
    },
  },
  COMPETITIVE_ANALYSIS: {
    COMPETITORS: 'competitiveAnalysis.competitors',
    COMPARISON_ITEM: 'competitiveAnalysis.comparisonItem',
    KEY_COMPETITORS: 'competitiveAnalysis.keyCompetitors',
    COMPETITORS_QUALITIES: 'competitiveAnalysis.competitorsQualities',
    YOUR_COMPANY_QUALITIES: {
      FILE: 'competitiveAnalysis.yourCompanyQualities.file',
      STRENGTH: 'competitiveAnalysis.yourCompanyQualities.strength',
      WEAKNESSES: 'competitiveAnalysis.yourCompanyQualities.weaknesses',
    },
    BARRIERS: 'competitiveAnalysis.barriers',
    ATTRACTING_COMPETITORS_CUSTOMERS: 'competitiveAnalysis.attractingCompetitorsCustomers',
    COMPETITORS_DIFFERENCE: 'competitiveAnalysis.competitorsDifference',
    PURCHASING_PRODUCTS: 'competitiveAnalysis.purchasingProducts',
    FUTURE_EXPECTATIONS: 'competitiveAnalysis.futureExpectations',
    CHANGING_OF_CURRENT_MARKET: 'competitiveAnalysis.changingOfCurrentMarket',
  },
  SALES_PLAN: {
    SALES_CHANNELS: 'salesPlan.salesChannels',
    STAGES: 'salesPlan.stages',
    DETAILED_SALES_CHANNEL: 'salesPlan.detailedSalesChannel',
    GOALS: 'salesPlan.goals',
    AFFECT_FACTORS: 'salesPlan.affectFactors',
    MOTIVATION_SYSTEM: 'salesPlan.motivationSystem',
    SALES_TEAM_STRUCTURE: 'salesPlan.salesTeamStructure',
    PERFORMANCE_INDICTORS: 'salesPlan.performanceIndictors',
    PLANNED_PRODUCTS_VOLUME: 'salesPlan.plannedProductsVolume',
    TOTAL_PLANNED_PRODUCTS_VOLUME: 'salesPlan.totalPlannedProductsVolume',
  },
  MARKETING_PLAN: {
    ADVERTISING_CAMPAIGNS: 'marketingPlan.advertisingCampaigns',
    POSITIONING: 'marketingPlan.positioning',
    PRICING_STRATEGY: 'marketingPlan.pricingStrategy',
    TOOLS_AND_TECHNOLOGIES: 'marketingPlan.toolsAndTechnologies',
    BRAND_ELEMENTS: 'marketingPlan.brandElements',
    BUDGET: 'marketingPlan.budget',
  },
  MANAGEMENT_TEAM: {
    HISTORY: 'managementTeam.history',
    SKILLS: 'managementTeam.skills',
    PERSONNEL_PROFILES: 'managementTeam.personnelProfiles',
  },
  EXTERNAL_PARTNERS_AND_ADVISERS: {
    PARTNERS: 'externalPartnersAndAdvisers.partners',
    CRITERIA: 'externalPartnersAndAdvisers.criteria',
    PLANS: 'externalPartnersAndAdvisers.plans',
    LIST_OF_EXTERNAL_PARTNERS: 'externalPartnersAndAdvisers.listOfExternalPartners',
  },
  STAFF_ORGANIZATION: {
    NUMBER_OF_STAFF: 'staffOrganization.numberOfStaff',
    LEVEL1_DEPARTMENT: 'staffOrganization.level1Department',
    LEVEL2_DEPARTMENT: 'staffOrganization.level2Department',
    LEVEL3_DEPARTMENT: 'staffOrganization.level3Department',
    ADDITIONAL_HIRES: 'staffOrganization.additionalHires',
    SALARY_POLICY: 'staffOrganization.salaryPolicy',
  },
  PLANNED_EXPENSES: {
    MONTHLY_EXPENSES: {
      NON_OPERATING_EXPENSES: 'plannedExpenses.monthlyExpenses.nonOperatingExpenses',
      NON_OPERATING_REVENUE: 'plannedExpenses.monthlyExpenses.nonOperatingRevenue',
      NON_OPERATING_GROWTH_RATE: 'plannedExpenses.monthlyExpenses.nonOperatingGrowthRate',
    },
  },
  OPENING_BALANCE_SHEETS: {
    INITIAL_LIABILITIES: {
      CURRENT_LIABILITIES: {
        PERCENT_CREDIT_PURCHASES: 'openingBalanceSheets.initialLiabilities.currentLiabilities.percentCreditPurchases',
        SHORT_TERM_DEBT: 'openingBalanceSheets.initialLiabilities.currentLiabilities.shortTermDebt',
        CURRENT_PORTION_LONG_TERM_DEBT:
          'openingBalanceSheets.initialLiabilities.currentLiabilities.currentPortionLongTermDebt',
        ACCRUED_LIABILITIES: 'openingBalanceSheets.initialLiabilities.currentLiabilities.accruedLiabilities',
        DEFERRED_TAX_LIABILITIES: 'openingBalanceSheets.initialLiabilities.currentLiabilities.deferredTaxLiabilities',
        PROVISIONS: 'openingBalanceSheets.initialLiabilities.currentLiabilities.provisions',
        OTHER_CURRENT_LIABILITIES: 'openingBalanceSheets.initialLiabilities.currentLiabilities.otherCurrentLiabilities',
      },
      NON_CURRENT_LIABILITIES: {
        DEFERRED_TAX_LIABILITIES:
          'openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.deferredTaxLiabilities',
        PROVISIONS_NON_CURRENT: 'openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.provisionsNonCurrent',
        OTHER_NON_CURRENT_LIABILITIES:
          'openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.otherNonCurrentLiabilities',
      },
    },
    INITIAL_EQUITY: {
      ADDITIONAL_PAID_IN_CAPITAL: 'openingBalanceSheets.initialEquity.additionalPaidInCapital',
      RESERVES: 'openingBalanceSheets.initialEquity.reserves',
      ACCUMULATED_OTHER_COMPREHENSIVE_INCOME: 'openingBalanceSheets.initialEquity.accumulatedOtherComprehensiveIncome',
      RETAINED_EARNINGS: 'openingBalanceSheets.initialEquity.retainedEarnings',
    },
  },
} as const;

interface ICompanyRowForm {
  [STRENGTH_WEAKNESSES_NAMES.TITLE]: string;
}

export interface IGlobalSection {
  [GLOBAL_FIELD_NAMES.COMPANY_NAME]: string;
  [GLOBAL_FIELD_NAMES.REGISTRATION_DATE]: string;
  [GLOBAL_FIELD_NAMES.ADDRESS]: string;
  [GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION]: string;

  [GLOBAL_FIELD_NAMES.COMPANY_SECTOR]?: string | IOptionType | null;
  [GLOBAL_FIELD_NAMES.COMPANY_SUB_SECTOR]?: string | null;
  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY_GROUP]?: string | null;
  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY]?: string | null;

  [GLOBAL_FIELD_NAMES.NUMBER_OF_EMPLOYEES]: string;
  [GLOBAL_FIELD_NAMES.ANNUAL_INCOME]: string;
  [GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION]: string;
  [GLOBAL_FIELD_NAMES.COVER_TITLE]: string;
  [GLOBAL_FIELD_NAMES.MAIN_PRODUCTS_OR_SERVICES]: string;
  [GLOBAL_FIELD_NAMES.PERIOD]: IPeriodNames;
  [GLOBAL_FIELD_NAMES.PERIOD_TABLE_HEADINGS]: IPeriodsTableRow;
  [GLOBAL_FIELD_NAMES.CURRENCY]: CurrencyType;
  [GLOBAL_FIELD_NAMES.UNIT]: IDimensionalityOption;
  [GLOBAL_FIELD_NAMES.MAIN_GOALS]: string;
  [GLOBAL_FIELD_NAMES.STRATEGY_FOR_ACHIEVING_GOALS]: string;
  [GLOBAL_FIELD_NAMES.TARGET_MARKET]: string;
  [GLOBAL_FIELD_NAMES.TARGET_AUDIENCE]: string;
  [GLOBAL_FIELD_NAMES.TARGET_CHANNELS]: string;
  [GLOBAL_FIELD_NAMES.MAIN_COMPETITORS]: string;
  [GLOBAL_FIELD_NAMES.PROJECTED_ANNUAL_INCOME]: string;
  [GLOBAL_FIELD_NAMES.FORECASTED_PROFITABILITY]: string;
  [GLOBAL_FIELD_NAMES.PROJECT_PAYBACK_PERIOD]: string;
  [GLOBAL_FIELD_NAMES.TOTAL_COST]: string;
  [GLOBAL_FIELD_NAMES.FUNDING_AMOUNT]: string;
  [GLOBAL_FIELD_NAMES.PERCENTAGE_OF_FUNDING]: string;
  [GLOBAL_FIELD_NAMES.MISSION]: string;
  [GLOBAL_FIELD_NAMES.COMPETITIVE_ADVANTAGE]: string;
  [GLOBAL_FIELD_NAMES.PRODUCT_DEVELOPMENT_PLANS]: string;
  [GLOBAL_FIELD_NAMES.LOAN_BORROW_AMOUNT]: string;
  [GLOBAL_FIELD_NAMES.INVESTMENT_AMOUNT]: string;
  [GLOBAL_FIELD_NAMES.COMPANY_STRENGTHS]: ICompanyRowForm[];
  [GLOBAL_FIELD_NAMES.COMPANY_WEAKNESSES]: ICompanyRowForm[];
  [GLOBAL_FIELD_NAMES.PRODUCTS]?: IGlobalProductEntity[];
  [GLOBAL_FIELD_NAMES.MARKETING_STRATEGY]: string;
  [GLOBAL_FIELD_NAMES.REVENUE_STREAMS]: string;
  [GLOBAL_FIELD_NAMES.IS_JUST_CREATED]: boolean;
  [GLOBAL_FIELD_NAMES.DOCUMENT_LANGUAGE]: string;
}

export const strengthWeaknessesInitialValues: ICompanyRowForm = {
  [STRENGTH_WEAKNESSES_NAMES.TITLE]: '',
};

export const globalInitialValues: IGlobalSection = {
  [GLOBAL_FIELD_NAMES.COMPANY_NAME]: '',
  [GLOBAL_FIELD_NAMES.REGISTRATION_DATE]: '',
  [GLOBAL_FIELD_NAMES.ADDRESS]: '',
  [GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION]: '',

  [GLOBAL_FIELD_NAMES.COMPANY_SECTOR]: null,
  [GLOBAL_FIELD_NAMES.COMPANY_SUB_SECTOR]: null,
  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY_GROUP]: null,
  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY]: null,

  [GLOBAL_FIELD_NAMES.NUMBER_OF_EMPLOYEES]: '',
  [GLOBAL_FIELD_NAMES.ANNUAL_INCOME]: '',
  [GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION]: '',
  [GLOBAL_FIELD_NAMES.COVER_TITLE]: '',
  [GLOBAL_FIELD_NAMES.MAIN_PRODUCTS_OR_SERVICES]: '',
  [GLOBAL_FIELD_NAMES.PERIOD]: PERIOD_NAMES.QUARTER_VALUES,
  [GLOBAL_FIELD_NAMES.PERIOD_TABLE_HEADINGS]: initialPeriodsTableHeadingRow,
  [GLOBAL_FIELD_NAMES.CURRENCY]: CURRENCY_LIST[0],
  [GLOBAL_FIELD_NAMES.UNIT]: DIMENSIONALITY_VALUES.SINGLE,
  [GLOBAL_FIELD_NAMES.MAIN_GOALS]: '',
  [GLOBAL_FIELD_NAMES.STRATEGY_FOR_ACHIEVING_GOALS]: '',
  [GLOBAL_FIELD_NAMES.TARGET_MARKET]: '',
  [GLOBAL_FIELD_NAMES.TARGET_AUDIENCE]: '',
  [GLOBAL_FIELD_NAMES.TARGET_CHANNELS]: '',
  [GLOBAL_FIELD_NAMES.MAIN_COMPETITORS]: '',
  [GLOBAL_FIELD_NAMES.PROJECTED_ANNUAL_INCOME]: '',
  [GLOBAL_FIELD_NAMES.FORECASTED_PROFITABILITY]: '',
  [GLOBAL_FIELD_NAMES.PROJECT_PAYBACK_PERIOD]: '',
  [GLOBAL_FIELD_NAMES.TOTAL_COST]: '',
  [GLOBAL_FIELD_NAMES.FUNDING_AMOUNT]: '',
  [GLOBAL_FIELD_NAMES.PERCENTAGE_OF_FUNDING]: '',
  [GLOBAL_FIELD_NAMES.MISSION]: '',
  [GLOBAL_FIELD_NAMES.COMPETITIVE_ADVANTAGE]: '',
  [GLOBAL_FIELD_NAMES.PRODUCT_DEVELOPMENT_PLANS]: '',
  [GLOBAL_FIELD_NAMES.LOAN_BORROW_AMOUNT]: '',
  [GLOBAL_FIELD_NAMES.INVESTMENT_AMOUNT]: '',
  [GLOBAL_FIELD_NAMES.COMPANY_STRENGTHS]: [strengthWeaknessesInitialValues],
  [GLOBAL_FIELD_NAMES.COMPANY_WEAKNESSES]: [strengthWeaknessesInitialValues],
  [GLOBAL_FIELD_NAMES.PRODUCTS]: [productInitialValues],
  [GLOBAL_FIELD_NAMES.MARKETING_STRATEGY]: '',
  [GLOBAL_FIELD_NAMES.REVENUE_STREAMS]: '',
  [GLOBAL_FIELD_NAMES.IS_JUST_CREATED]: true,
  [GLOBAL_FIELD_NAMES.DOCUMENT_LANGUAGE]: 'English',
};

export const useGetGlobalInitialValues = (): IGlobalSection => {
  const periodTableHeadings = useGetInitialPeriodsTableHeadingRow();

  return useMemo(
    () => ({
      ...globalInitialValues,
      [GLOBAL_FIELD_NAMES.PERIOD_TABLE_HEADINGS]: periodTableHeadings,
    }),
    [periodTableHeadings],
  );
};

export const SECTOR_FIELD_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_SECTOR);
export const SUB_SECTOR_FIELD_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_SUB_SECTOR);
export const COMPANY_INDUSTRY_GROUP_FIELD_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY_GROUP);
export const INDUSTRY_FIELD_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY);
export const GLOBAL_PERIOD_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PERIOD);
export const GLOBAL_PERIOD_TABLE_HEADINGS_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PERIOD_TABLE_HEADINGS);
export const GLOBAL_CURRENCY_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.CURRENCY);
export const GLOBAL_UNIT_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.UNIT);
export const GLOBAL_PRODUCTS_NAME = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PRODUCTS);
export const isJustCreatedFiled = concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.IS_JUST_CREATED);
